import { useState } from "react";
import { toast } from "sonner";
import { Loader2, Music2, Apple, ExternalLink, Link2, X } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import SpotifyPicker from "./SpotifyPicker";

export default function ArtistProfile() {
  const { user, setUser } = useAuth();
  const [form, setForm] = useState({
    display_name: user?.display_name || user?.name || "",
    genre: user?.genre || "",
    website: user?.website || "",
    bio: user?.bio || "",
  });
  // Global streaming profile links (persisted via PATCH /artist/profile)
  const [spotify, setSpotify] = useState({
    id: user?.spotify_artist_id || "",
    name: user?.spotify_artist_name || "",
    url: user?.spotify_artist_url || "",
    image: user?.spotify_image_url || "",
  });
  const [appleMusicUrl, setAppleMusicUrl] = useState(user?.apple_music_artist_url || "");
  const [pickerOpen, setPickerOpen] = useState(false);
  const [appleBusy, setAppleBusy] = useState(false);
  const [saving, setSaving] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const attachApple = async () => {
    const url = appleMusicUrl.trim();
    if (!url) return;
    setAppleBusy(true);
    try {
      const { data } = await api.post("/apple_music/from_url", { url });
      setAppleMusicUrl(data.url);
      toast.success("Apple Music artist linked");
    } catch (err) {
      toast.error(formatApiError(err, "That URL didn't work"));
    } finally {
      setAppleBusy(false);
    }
  };

  const unlinkSpotify = () => setSpotify({ id: "", name: "", url: "", image: "" });
  const unlinkApple = () => setAppleMusicUrl("");

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...form,
        spotify_artist_id: spotify.id,
        spotify_artist_name: spotify.name,
        spotify_artist_url: spotify.url,
        spotify_image_url: spotify.image,
        apple_music_artist_url: appleMusicUrl,
      };
      const { data } = await api.patch("/artist/profile", payload);
      setUser(data);
      toast.success("Profile updated");
    } catch (err) {
      toast.error(formatApiError(err, "Failed to save profile"));
    } finally {
      setSaving(false);
    }
  };

  const initial = (form.display_name || user?.email || "?").charAt(0).toUpperCase();
  const inputCls = "w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-blue-500/50 placeholder:text-slate-500";
  const labelCls = "text-xs tracking-[0.15em] uppercase font-bold text-slate-500 mb-2 block";

  return (
    <div className="max-w-2xl space-y-8" data-testid="artist-profile">
      <div className="flex items-center gap-5">
        {user?.picture ? (
          <img src={user.picture} alt="" className="h-20 w-20 rounded-2xl object-cover" />
        ) : (
          <div className="h-20 w-20 rounded-2xl bg-blue-600 flex items-center justify-center text-2xl font-medium text-white">{initial}</div>
        )}
        <div>
          <h2 className="font-display text-2xl tracking-tight text-white">{form.display_name || user?.name}</h2>
          <p className="text-slate-500 text-sm mt-1">{user?.email}</p>
        </div>
      </div>

      <form onSubmit={save} className="rounded-2xl border border-white/10 bg-white/[0.02] p-6 md:p-8 space-y-5" data-testid="profile-form">
        <div>
          <label className={labelCls}>Display name</label>
          <input data-testid="profile-name" value={form.display_name} onChange={update("display_name")} placeholder="Your artist or label name" className={inputCls} />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          <div>
            <label className={labelCls}>Primary genre</label>
            <input data-testid="profile-genre" value={form.genre} onChange={update("genre")} placeholder="e.g. Electronic" className={inputCls} />
          </div>
          <div>
            <label className={labelCls}>Website</label>
            <input data-testid="profile-website" value={form.website} onChange={update("website")} placeholder="https://" className={inputCls} />
          </div>
        </div>
        <div>
          <label className={labelCls}>Bio</label>
          <textarea data-testid="profile-bio" value={form.bio} onChange={update("bio")} rows={4} placeholder="Tell listeners about your sound" className={`${inputCls} resize-none`} />
        </div>

        {/* Streaming profile links */}
        <div className="pt-2">
          <label className={labelCls}>Streaming profiles</label>
          <p className="text-xs text-slate-500 -mt-1 mb-3">
            These get attached to every new release so DSPs can map them to the correct artist page.
          </p>

          {/* Spotify card */}
          <div
            data-testid="spotify-link-card"
            className={`rounded-2xl border p-4 flex items-center gap-4 transition-colors ${
              spotify.url
                ? "border-emerald-500/40 bg-emerald-500/[0.05]"
                : "border-white/10 bg-white/[0.02]"
            }`}
          >
            {spotify.image ? (
              <img src={spotify.image} alt="" className="h-12 w-12 rounded-full object-cover border border-white/10" />
            ) : (
              <div className="h-12 w-12 rounded-full bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center">
                <Music2 size={20} className="text-emerald-300" />
              </div>
            )}
            <div className="min-w-0 flex-1">
              <div className="font-display text-base text-white flex items-center gap-2">
                Spotify for Artists
                {spotify.url && (
                  <span className="text-[9px] uppercase tracking-widest font-bold text-emerald-300 bg-emerald-500/10 border border-emerald-500/25 rounded-full px-2 py-0.5">Linked</span>
                )}
              </div>
              {spotify.url ? (
                <a
                  href={spotify.url}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1 text-xs text-emerald-300 hover:text-emerald-200 truncate max-w-[220px] sm:max-w-none"
                  data-testid="spotify-linked-url"
                >
                  {spotify.name || spotify.url} <ExternalLink size={11} />
                </a>
              ) : (
                <p className="text-xs text-slate-500 mt-0.5">Not linked yet — search or paste your Spotify artist URL.</p>
              )}
            </div>
            {spotify.url ? (
              <button
                type="button"
                data-testid="spotify-unlink"
                onClick={unlinkSpotify}
                className="inline-flex items-center gap-1.5 rounded-full border border-white/10 text-slate-300 hover:bg-white/5 text-xs px-3 py-1.5"
              >
                <X size={12} /> Unlink
              </button>
            ) : (
              <button
                type="button"
                data-testid="spotify-link-btn"
                onClick={() => setPickerOpen(true)}
                className="inline-flex items-center gap-1.5 rounded-full bg-white text-black text-xs font-medium px-3 py-1.5 hover:bg-slate-200"
              >
                <Link2 size={12} /> Link Spotify
              </button>
            )}
          </div>

          {/* Apple Music card */}
          <div
            data-testid="apple-music-link-card"
            className={`mt-3 rounded-2xl border p-4 flex items-center gap-4 transition-colors ${
              appleMusicUrl
                ? "border-pink-500/40 bg-pink-500/[0.05]"
                : "border-white/10 bg-white/[0.02]"
            }`}
          >
            <div className="h-12 w-12 rounded-full bg-pink-500/15 border border-pink-500/30 flex items-center justify-center shrink-0">
              <Apple size={20} className="text-pink-200" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="font-display text-base text-white flex items-center gap-2">
                Apple Music for Artists
                {appleMusicUrl && (
                  <span className="text-[9px] uppercase tracking-widest font-bold text-pink-200 bg-pink-500/10 border border-pink-500/25 rounded-full px-2 py-0.5">Linked</span>
                )}
              </div>
              <div className="mt-1 flex items-center gap-2">
                <input
                  data-testid="apple-music-url"
                  value={appleMusicUrl}
                  onChange={(e) => setAppleMusicUrl(e.target.value)}
                  placeholder="https://music.apple.com/us/artist/…/1234567890"
                  className="flex-1 bg-white/5 border border-white/10 rounded-lg px-2.5 py-2 text-xs text-white focus:outline-none focus:ring-2 focus:ring-pink-500/40 placeholder:text-slate-500"
                />
                <button
                  type="button"
                  data-testid="apple-music-attach"
                  onClick={attachApple}
                  disabled={appleBusy || !appleMusicUrl.trim()}
                  className="inline-flex items-center gap-1.5 rounded-full bg-white text-black text-xs font-medium px-3 py-1.5 hover:bg-slate-200 disabled:opacity-60"
                >
                  {appleBusy ? <Loader2 size={12} className="animate-spin" /> : <><Link2 size={12} /> Verify</>}
                </button>
                {appleMusicUrl && (
                  <button
                    type="button"
                    data-testid="apple-music-unlink"
                    onClick={unlinkApple}
                    className="inline-flex items-center gap-1.5 rounded-full border border-white/10 text-slate-300 hover:bg-white/5 text-xs px-3 py-1.5"
                    aria-label="Unlink Apple Music"
                  >
                    <X size={12} />
                  </button>
                )}
              </div>
              <p className="text-[11px] text-slate-500 mt-1.5">Paste the URL from music.apple.com/us/artist/…</p>
            </div>
          </div>
        </div>

        <button
          data-testid="profile-save"
          type="submit"
          disabled={saving}
          className="inline-flex items-center justify-center gap-2 rounded-full bg-white text-black font-medium px-7 py-3 hover:bg-slate-200 disabled:opacity-60 transition-colors"
        >
          {saving ? <Loader2 size={18} className="animate-spin" /> : "Save changes"}
        </button>
      </form>

      {pickerOpen && (
        <SpotifyPicker
          initialQuery={form.display_name}
          onClose={() => setPickerOpen(false)}
          onPick={(match) => {
            setSpotify({
              id: match.id || "",
              name: match.name || "",
              url: match.url || "",
              image: match.image || "",
            });
            setPickerOpen(false);
            toast.success("Spotify artist linked");
          }}
        />
      )}
    </div>
  );
}
